import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { AlertTriangle, Shield, TrendingDown, Zap, Info, DollarSign } from 'lucide-react';
import { calcPayToPlay } from '@/utils/equityMath';

interface PayToPlayInputs {
  currentShares: number;
  liquidationPreference: number;
  antiDilutionProtection: boolean;
  votingRights: string; // 'preferred' or 'common'

  newRoundSize: number;
  newRoundPrice: number;
  yourProRataAmount: number;
  totalSharesBeforeRound: number;
  /** Assumed exit price per share. Previously hardcoded at $10. */
  exitPricePerShare: number;
}

interface Scenario {
  name: string;
  participates: boolean;
  sharesAfter: number;
  ownershipAfter: number;
  liquidationPrefAfter: number;
  antiDilutionProtected: boolean;
  votingPower: string;
  economicValue: number;
  color: string;
}

export function PayToPlayCalculator() {
  const [inputs, setInputs] = useState<PayToPlayInputs>({
    currentShares: 100000,
    liquidationPreference: 1000000,
    antiDilutionProtection: true,
    votingRights: 'preferred',

    newRoundSize: 5000000,
    newRoundPrice: 5.0,
    yourProRataAmount: 500000,
    totalSharesBeforeRound: 1000000,
    exitPricePerShare: 10.0,
  });

  const [participate, setParticipate] = useState<boolean | null>(null);
  const [participationAmount, setParticipationAmount] = useState(0);

  // The maths lives in calcPayToPlay so it can be tested. This component used to
  // carry its own copy, which hardcoded a $10/share exit regardless of the round
  // price, never read the anti-dilution or voting inputs, and took current
  // ownership as a free-text field that could contradict the share counts.
  const analysis = useMemo(() => {
    const result = calcPayToPlay({
      currentShares: inputs.currentShares,
      liquidationPreference: inputs.liquidationPreference,
      hasAntiDilution: inputs.antiDilutionProtection,
      hasPreferredVoting: inputs.votingRights === 'preferred',
      newRoundSize: inputs.newRoundSize,
      newRoundPrice: inputs.newRoundPrice,
      yourProRataAmount: inputs.yourProRataAmount,
      totalSharesBeforeRound: inputs.totalSharesBeforeRound,
      participationAmount,
      exitPricePerShare: inputs.exitPricePerShare,
    });

    const palette = ['hsl(var(--chart-2))', 'hsl(var(--primary))', 'hsl(var(--destructive))'];

    const scenarios: Scenario[] = result.scenarios.map((s, i) => ({
      name: s.name,
      participates: s.participates,
      sharesAfter: s.sharesAfter,
      ownershipAfter: s.ownershipAfter,
      liquidationPrefAfter: s.liquidationPrefAfter,
      antiDilutionProtected: s.keepsAntiDilution,
      votingPower: s.keepsPreferredVoting ? 'Preferred (Super-Majority)' : 'Common (Majority)',
      economicValue: s.economicValue,
      color: i === 1 && !s.participates ? 'hsl(var(--chart-4))' : palette[i],
    }));

    return {
      scenarios,
      proRataPercentage: result.proRataPct,
      minimumThreshold: result.minimumThreshold,
      dilution: result.dilutionPct,
      valueLoss: result.valueLoss,
      newSharesIssued: result.newSharesIssued,
      currentOwnershipPct: result.currentOwnershipPct,
      exitValuation: result.exitValuation,
    };
  }, [inputs, participationAmount]);

  const chartData = analysis.scenarios.map((s) => ({
    name: s.name,
    ownership: s.ownershipAfter,
    economicValue: s.economicValue / 1000000, // In millions
    liquidationPref: s.liquidationPrefAfter / 1000000,
    color: s.color,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Pay-to-Play Calculator
          <Badge variant="destructive" className="ml-auto">
            <AlertTriangle className="h-3 w-3 mr-1" />
            High Stakes
          </Badge>
        </CardTitle>
        <CardDescription>
          Model the consequences of not participating in future funding rounds
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Warning Alert */}
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>What is Pay-to-Play?</AlertTitle>
          <AlertDescription>
            Pay-to-play provisions penalize investors who don't participate in future rounds by: •
            Converting preferred stock to common (losing liquidation preference) • Eliminating
            anti-dilution protection • Reducing voting rights • Significantly reducing economic
            value at exit
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="calculator" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="calculator">Calculator</TabsTrigger>
            <TabsTrigger value="comparison">Scenario Analysis</TabsTrigger>
            <TabsTrigger value="learn">Understanding P2P</TabsTrigger>
          </TabsList>

          <TabsContent value="calculator" className="space-y-6">
            {/* Current Position */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Your Current Position</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currentShares">Current Shares Owned</Label>
                  <Input
                    id="currentShares"
                    type="number"
                    value={inputs.currentShares}
                    onChange={(e) =>
                      setInputs({ ...inputs, currentShares: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Ownership %</Label>
                  <Input value={analysis.currentOwnershipPct.toFixed(2)} readOnly disabled />
                  <p className="text-xs text-muted-foreground">
                    Derived from your shares and the total before the round
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="exitPricePerShare">Assumed Exit Price per Share ($)</Label>
                  <Input
                    id="exitPricePerShare"
                    type="number"
                    step="0.01"
                    value={inputs.exitPricePerShare}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        exitPricePerShare: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Every value comparison below depends on this. Against a round priced at $
                    {inputs.newRoundPrice} a share, it assumes a{' '}
                    {inputs.newRoundPrice > 0
                      ? (inputs.exitPricePerShare / inputs.newRoundPrice).toFixed(2)
                      : '—'}
                    x exit.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="liquidationPreference">Liquidation Preference ($)</Label>
                  <Input
                    id="liquidationPreference"
                    type="number"
                    value={inputs.liquidationPreference}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        liquidationPreference: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Current Rights</Label>
                  <div className="flex items-center space-x-4 pt-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="antiDilution"
                        checked={inputs.antiDilutionProtection}
                        onCheckedChange={(checked) =>
                          setInputs({ ...inputs, antiDilutionProtection: checked })
                        }
                      />
                      <Label htmlFor="antiDilution" className="text-sm">
                        Anti-Dilution
                      </Label>
                    </div>
                    <Badge variant="secondary">
                      {inputs.votingRights === 'preferred' ? 'Preferred Voting' : 'Common Voting'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* New Round Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Upcoming Funding Round</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="newRoundSize">Round Size ($)</Label>
                  <Input
                    id="newRoundSize"
                    type="number"
                    value={inputs.newRoundSize}
                    onChange={(e) =>
                      setInputs({ ...inputs, newRoundSize: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newRoundPrice">Price per Share ($)</Label>
                  <Input
                    id="newRoundPrice"
                    type="number"
                    step="0.01"
                    value={inputs.newRoundPrice}
                    onChange={(e) =>
                      setInputs({ ...inputs, newRoundPrice: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="yourProRataAmount">Your Pro-Rata Amount ($)</Label>
                  <Input
                    id="yourProRataAmount"
                    type="number"
                    value={inputs.yourProRataAmount}
                    onChange={(e) =>
                      setInputs({ ...inputs, yourProRataAmount: parseFloat(e.target.value) || 0 })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {analysis.proRataPercentage.toFixed(1)}% of round to maintain ownership
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totalSharesBeforeRound">Total Shares Outstanding</Label>
                  <Input
                    id="totalSharesBeforeRound"
                    type="number"
                    value={inputs.totalSharesBeforeRound}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        totalSharesBeforeRound: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Decision Section */}
            <Card className="border-2 border-primary">
              <CardHeader>
                <CardTitle className="text-lg">Your Participation Decision</CardTitle>
                <CardDescription>Choose your participation level</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="participationAmount" className="text-base">
                    Investment Amount ($)
                  </Label>
                  <Badge variant="outline" className="ml-2">
                    Min: ${analysis.minimumThreshold.toLocaleString()} (50% of pro-rata)
                  </Badge>
                </div>
                <Input
                  id="participationAmount"
                  type="number"
                  value={participationAmount}
                  onChange={(e) => setParticipationAmount(parseFloat(e.target.value) || 0)}
                  placeholder={`Enter amount (0 to ${inputs.yourProRataAmount})`}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setParticipationAmount(0)}
                    className="flex-1"
                  >
                    Don't Participate
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setParticipationAmount(analysis.minimumThreshold)}
                    className="flex-1"
                  >
                    Minimum (50%)
                  </Button>
                  <Button
                    onClick={() => setParticipationAmount(inputs.yourProRataAmount)}
                    className="flex-1"
                  >
                    Full Pro-Rata
                  </Button>
                </div>

                {participationAmount > 0 && participationAmount < analysis.minimumThreshold && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Below minimum threshold! You'll be treated as non-participating and lose
                      preferred rights.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            {participationAmount >= 0 && (
              <div className="space-y-6 mt-6">
                <h3 className="text-lg font-semibold">Impact Analysis</h3>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">New Ownership</div>
                      <div className="text-2xl font-bold">
                        {analysis.scenarios[1].ownershipAfter.toFixed(2)}%
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        vs {analysis.currentOwnershipPct.toFixed(2)}% current
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Liquidation Pref</div>
                      <div className="text-2xl font-bold">
                        ${(analysis.scenarios[1].liquidationPrefAfter / 1000000).toFixed(1)}M
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {analysis.scenarios[1].liquidationPrefAfter > 0 ? 'Protected' : 'Lost'}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Anti-Dilution</div>
                      <div className="text-2xl font-bold flex items-center gap-2">
                        {analysis.scenarios[1].antiDilutionProtected ? (
                          <Shield className="h-6 w-6 text-green-500" />
                        ) : (
                          <TrendingDown className="h-6 w-6 text-red-500" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {analysis.scenarios[1].antiDilutionProtected ? 'Protected' : 'Lost'}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Economic Value</div>
                      <div className="text-2xl font-bold text-primary">
                        ${(analysis.scenarios[1].economicValue / 1000000).toFixed(1)}M
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">At $10/share exit</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Visual Comparison */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Scenario Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-15} textAnchor="end" height={80} />
                        <YAxis
                          yAxisId="left"
                          label={{ value: 'Value ($M)', angle: -90, position: 'insideLeft' }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          label={{ value: 'Ownership %', angle: 90, position: 'insideRight' }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                          yAxisId="left"
                          dataKey="economicValue"
                          fill="hsl(var(--primary))"
                          name="Economic Value ($M)"
                        />
                        <Bar
                          yAxisId="left"
                          dataKey="liquidationPref"
                          fill="hsl(var(--chart-3))"
                          name="Liq Pref ($M)"
                        />
                        <Bar
                          yAxisId="right"
                          dataKey="ownership"
                          fill="hsl(var(--chart-2))"
                          name="Ownership %"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Warning if not participating enough */}
                {participationAmount < analysis.minimumThreshold && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Critical Impact Warning</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>By not meeting the minimum participation threshold, you will:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>
                          Lose ${(inputs.liquidationPreference / 1000000).toFixed(1)}M in
                          liquidation preference
                        </li>
                        <li>
                          Lose anti-dilution protection worth potentially millions in a down round
                        </li>
                        <li>Lose preferred voting rights (super-majority → majority)</li>
                        <li>
                          Reduce exit value by ${(analysis.valueLoss / 1000000).toFixed(1)}M (at
                          $10/share)
                        </li>
                      </ul>
                      <p className="font-semibold mt-2">
                        Consider investing at least ${analysis.minimumThreshold.toLocaleString()} to
                        maintain your preferred status.
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="comparison" className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scenario</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                  <TableHead className="text-right">Ownership</TableHead>
                  <TableHead className="text-right">Liq Pref</TableHead>
                  <TableHead>Anti-Dilution</TableHead>
                  <TableHead>Voting</TableHead>
                  <TableHead className="text-right">Exit Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analysis.scenarios.map((scenario, index) => (
                  <TableRow key={scenario.name} className={index === 2 ? 'bg-destructive/5' : ''}>
                    <TableCell className="font-medium">{scenario.name}</TableCell>
                    <TableCell className="text-right font-mono">
                      {scenario.sharesAfter.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {scenario.ownershipAfter.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">
                      ${(scenario.liquidationPrefAfter / 1000000).toFixed(1)}M
                    </TableCell>
                    <TableCell>
                      {scenario.antiDilutionProtected ? (
                        <Badge className="bg-green-500">✓ Protected</Badge>
                      ) : (
                        <Badge variant="destructive">✗ Lost</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          scenario.votingPower.includes('Preferred') ? 'default' : 'secondary'
                        }
                      >
                        {scenario.votingPower}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ${(scenario.economicValue / 1000000).toFixed(2)}M
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Alert>
              <DollarSign className="h-4 w-4" />
              <AlertTitle>Value Comparison at Exit</AlertTitle>
              <AlertDescription>
                Full participation:{' '}
                <span className="font-bold">
                  ${(analysis.scenarios[0].economicValue / 1000000).toFixed(2)}M
                </span>
                {' vs '}
                No participation:{' '}
                <span className="font-bold">
                  ${(analysis.scenarios[2].economicValue / 1000000).toFixed(2)}M
                </span>
                {' = '}
                <span className="font-bold text-destructive">
                  ${(analysis.valueLoss / 1000000).toFixed(2)}M potential loss
                </span>
              </AlertDescription>
            </Alert>
          </TabsContent>

          <TabsContent value="learn" className="space-y-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">How Pay-to-Play Works</h3>
                <p className="text-sm text-muted-foreground">
                  Pay-to-play provisions are designed to ensure that existing investors continue to
                  support the company in future rounds. If you don't participate (or participate
                  below the threshold), your preferred stock is automatically converted to common or
                  "shadow preferred" stock.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">What You Lose</h3>
                <div className="space-y-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Liquidation Preference</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      You lose priority in exit scenarios. Instead of getting your money back first
                      (1x liquidation preference), you're treated like common stock and only get
                      your pro-rata share after all preferred shareholders are paid.
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Anti-Dilution Protection</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      You lose protection against down rounds. If the next round is at a lower
                      price, you won't receive additional shares to compensate, potentially losing
                      millions in value.
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Voting Rights</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      You lose super-majority voting rights. Preferred stock typically votes as a
                      separate class with veto rights on major decisions. Common stock only gets
                      regular majority voting.
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Board Seats & Information Rights</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      You may lose board observation rights and detailed information rights that
                      come with preferred stock, reducing your visibility into company operations.
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2">Typical Thresholds</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Participation Level</TableHead>
                      <TableHead>Outcome</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">100% of Pro-Rata</TableCell>
                      <TableCell>Full preferred rights maintained</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">50-99% of Pro-Rata</TableCell>
                      <TableCell>Usually maintains preferred rights</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{'<'} 50% of Pro-Rata</TableCell>
                      <TableCell className="text-destructive">
                        Conversion to common/shadow preferred
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Strategic Considerations</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                    <li>
                      Pay-to-play is common in down rounds or when companies need bridge financing
                    </li>
                    <li>
                      It's designed to prevent "free-riding" by investors who won't support the
                      company
                    </li>
                    <li>
                      Consider your total exposure and whether you want to invest more capital
                    </li>
                    <li>
                      Sometimes it's better to accept the conversion than over-concentrate your
                      portfolio
                    </li>
                    <li>Negotiate minimum thresholds before accepting pay-to-play provisions</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
